import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import { PageBody, PageHeader } from '@/components/AppShell'
import { ErrorState, TableSkeleton } from '@/components/states'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatAbsolute } from '@/lib/format'

import { runAction, useStaffMutation, useStaffZone, type ConnectionRow, type StaffAction } from '../api'
import { useStaff } from '../hooks'
import { beginBreakglass } from '../session'
import { MIN_BREAKGLASS_REASON } from '../tier'
import { ActionNotice, Fact, ReasonDialog, StaffGate, StaffLink, StateBadge } from '../ui'

interface Dialog {
  kind: 'pause' | 'unpause' | 'breakglass' | 'release' | 'reissue'
  connection?: ConnectionRow
}

export function StaffZonePage() {
  const { zoneId } = useParams()
  const staff = useStaff()
  const client = useQueryClient()
  const navigate = useNavigate()
  const zone = useStaffZone(zoneId)
  const [dialog, setDialog] = useState<Dialog | null>(null)
  const [notice, setNotice] = useState<{ title: string; notified: boolean | undefined } | null>(null)
  const act = useStaffMutation(({ action, reason }: { action: StaffAction; reason: string }) => runAction(action, reason))
  const data = zone.data
  const canOps = staff.can('zone_identity_ops')
  const paused = data?.zone_state !== 'ACTIVE'

  return (
    <StaffGate>
      <PageHeader
        eyebrow="Staff · Zone"
        title={data?.name ?? 'Zone'}
        description={
          data === undefined ? undefined : (
            <>
              of <StaffLink to={`/app/staff/accounts/${data.account_id}`}>{data.account_name}</StaffLink>{' '}
              <span className="font-mono text-xs">{data.id}</span>
            </>
          )
        }
        actions={
          data === undefined ? undefined : (
            <div className="flex flex-wrap justify-end gap-2">
              {staff.can('break_glass') ? (
                <Button variant="outline" onClick={() => { setDialog({ kind: 'breakglass' }) }}>Read messages</Button>
              ) : null}
              {canOps ? (
                paused ? (
                  <Button onClick={() => { setDialog({ kind: 'unpause' }) }}>Unpause zone</Button>
                ) : (
                  <Button variant="destructive" onClick={() => { setDialog({ kind: 'pause' }) }}>Pause zone</Button>
                )
              ) : null}
            </div>
          )
        }
      />
      <PageBody>
        <ActionNotice notice={notice} onDismiss={() => { setNotice(null) }} />
        {zone.isError ? (
          <ErrorState error={zone.error} title="Could not load this zone" onRetry={() => void zone.refetch()} />
        ) : data === undefined ? (
          <TableSkeleton rows={4} columns={3} />
        ) : (
          <>
            <Card>
              <CardContent>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3 lg:grid-cols-4">
                  <Fact label="State"><StateBadge state={data.zone_state} /></Fact>
                  <Fact label="Public id"><span className="font-mono text-xs">{data.public_id}</span></Fact>
                  <Fact label="Pending messages">{data.pending_message_count}</Fact>
                  <Fact label="Granted members">{data.granted_member_count}</Fact>
                  <Fact label="Retention">{data.retention_days === null ? 'account default' : `${data.retention_days} days`}</Fact>
                  <Fact label="Poll idle / active">{data.poll_idle_seconds}s / {data.poll_active_seconds}s</Fact>
                  <Fact label="Offline after">{data.offline_after_seconds}s</Fact>
                  <Fact label="Released after">{data.offline_release_days} days offline</Fact>
                  <Fact label="Created">{formatAbsolute(data.created_at)}</Fact>
                  <Fact label="State changed">{data.state_changed_at === null ? '—' : formatAbsolute(data.state_changed_at)}</Fact>
                </dl>
                {data.description === null || data.description === '' ? null : (
                  <p className="mt-4 text-sm text-muted-foreground">{data.description}</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Connections</CardTitle>
                <CardDescription>Names, states and timestamps only. Message bodies need break-glass.</CardDescription>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead>Transport</TableHead>
                      <TableHead className="text-right">Pending</TableHead>
                      <TableHead>Last activity</TableHead>
                      {canOps ? <TableHead /> : null}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.connections.map((c) => (
                      <TableRow key={c.id} data-testid="staff-connection-row">
                        <TableCell className="max-w-[14rem] truncate font-mono text-xs">{c.name}</TableCell>
                        <TableCell className="text-xs">{c.type}{c.is_identity ? ' · identity' : ''}</TableCell>
                        <TableCell>
                          <StateBadge state={c.connection_state} /> {c.paused ? <Badge variant="destructive">paused</Badge> : null}
                        </TableCell>
                        <TableCell className="text-xs">{c.has_transport ? 'connected' : 'none'}</TableCell>
                        <TableCell className="text-right tabular-nums">{c.pending_message_count}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{c.last_activity_at === null ? '—' : formatAbsolute(c.last_activity_at)}</TableCell>
                        {canOps ? (
                          <TableCell>
                            {c.is_identity ? (
                              <div className="flex justify-end gap-2">
                                <Button size="sm" variant="outline" onClick={() => { setDialog({ kind: 'reissue', connection: c }) }}>Reissue id</Button>
                                <Button size="sm" variant="destructive" onClick={() => { setDialog({ kind: 'release', connection: c }) }}>Release</Button>
                              </div>
                            ) : null}
                          </TableCell>
                        ) : null}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        )}
      </PageBody>

      {data === undefined || dialog === null ? null : dialog.kind === 'breakglass' ? (
        <ReasonDialog
          title="Read this zone’s messages"
          description={
            <>
              Break-glass opens <strong>{data.name}</strong>’s message bodies, read-only, for one hour. It also starts a read-only view of{' '}
              <strong>{data.account_name}</strong>. The grant and every page you read are audited, and the account’s administrators are emailed now.
            </>
          }
          confirmLabel="Grant break-glass"
          minLength={MIN_BREAKGLASS_REASON}
          destructive
          testId="breakglass-dialog"
          onClose={() => { setDialog(null) }}
          onConfirm={async (reason) => {
            await beginBreakglass(client, { id: data.id, accountId: data.account_id, accountName: data.account_name }, reason, staff.tier)
            void navigate('/app/messages')
          }}
        />
      ) : (
        <ReasonDialog
          title={
            dialog.kind === 'pause' ? `Pause ${data.name}?`
              : dialog.kind === 'unpause' ? `Unpause ${data.name}?`
                : dialog.kind === 'release' ? `Release ${dialog.connection?.name ?? ''}?`
                  : `Reissue ${dialog.connection?.name ?? ''}’s connection id?`
          }
          description={
            dialog.kind === 'pause'
              ? 'Everyone in the zone is disconnected; history stays readable. The account’s administrators are emailed with your reason.'
              : dialog.kind === 'unpause'
                ? 'The zone accepts connections again. The account’s administrators are emailed with your reason.'
                : dialog.kind === 'release'
                  ? 'Gives the name back: its queue is discarded and any parked receive ends. The account’s administrators are emailed with your reason.'
                  : 'Invalidates the identity’s connection id and watch URL. The new id is never shown to staff; its owner issues it again from their console. The account’s administrators are emailed with your reason.'
          }
          confirmLabel={
            dialog.kind === 'pause' ? 'Pause zone' : dialog.kind === 'unpause' ? 'Unpause zone' : dialog.kind === 'release' ? 'Release identity' : 'Reissue id'
          }
          destructive={dialog.kind !== 'unpause'}
          onClose={() => { setDialog(null) }}
          onConfirm={async (reason) => {
            const action: StaffAction =
              dialog.kind === 'pause' ? { kind: 'zone-pause', id: data.id }
                : dialog.kind === 'unpause' ? { kind: 'zone-unpause', id: data.id }
                  : dialog.kind === 'release' ? { kind: 'identity-release', zoneId: data.id, id: dialog.connection?.id ?? '' }
                    : { kind: 'identity-reissue', zoneId: data.id, id: dialog.connection?.id ?? '' }
            const r = await act.mutateAsync({ action, reason })
            setNotice({
              title: dialog.kind === 'pause' ? 'Zone paused' : dialog.kind === 'unpause' ? 'Zone unpaused' : dialog.kind === 'release' ? 'Identity released' : 'Connection id invalidated',
              notified: r.notified,
            })
          }}
        />
      )}
    </StaffGate>
  )
}
